import cx from 'classnames';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useState,
  type FocusEventHandler,
  type FunctionComponent,
  type KeyboardEventHandler,
  type Ref as ReactRef,
  type TextareaHTMLAttributes,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { useForwardRef } from '@trello/dom-hooks';
import { isSubmitEvent } from '@trello/keybindings';
import type { TestId } from '@trello/test-ids';

import { setHeight } from './setHeight';

import * as styles from './AutosizeTextarea.module.less';

const parseComputedCssLength = (value: string) => {
  // e.g. 12px -> 12 or normal -> 0
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toCssValue = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

const getLineHeight = (computedStyle: CSSStyleDeclaration) => {
  const lineHeight = parseComputedCssLength(computedStyle.lineHeight);
  if (lineHeight > 0) {
    return lineHeight;
  }

  return parseComputedCssLength(computedStyle.fontSize) * 1.5;
};

export type AutosizeTextareaProps =
  TextareaHTMLAttributes<HTMLTextAreaElement> & {
    shouldFocus?: boolean;
    bufferHeight?: number;
    minHeight?: number;
    /**
     * Show this many visible rows before enabling vertical scrolling.
     */
    maxVisibleRows?: number;
    /**
     * With `minHeight > 0`, pads the field so a single line is vertically centered in the
     * minimum height. Ignored when `minHeight` is not set.
     */
    verticallyCenter?: boolean;
    /** When true, scrollbar track/thumb are hidden; content can still scroll if needed. */
    hideScrollbar?: boolean;
    /** When true, disables the user-resizable corner handle (`resize: none`). */
    hideResizeHandle?: boolean;
    /**
     * Enter submits the parent form; Shift+Enter inserts a newline. Also shows
     * a "Shift + Return to add a new line" hint below the field unless
     * `submitHintId` is provided.
     */
    submitOnEnter?: boolean;
    /**
     * When set with `submitOnEnter`, the textarea is described by this id and
     * the hint is not rendered. Use with `AutosizeTextareaSubmitHint` so the
     * hint can sit outside a clipped field wrapper.
     */
    submitHintId?: string;
    testId?: TestId;
  };

export const AutosizeTextareaSubmitHint: FunctionComponent<{ id: string }> = ({
  id,
}) => (
  <div id={id} className={styles.newlineHint}>
    <FormattedMessage
      id="templates.autosize_textarea.shift-return-to-add-a-new-line"
      defaultMessage="<b>Shift + Return</b> to add a new line"
      description="Hint below a textarea that Enter submits and Shift+Return inserts a newline"
      values={{
        b: (chunks) => <b>{chunks}</b>,
      }}
    />
  </div>
);

export const AutosizeTextarea = forwardRef<
  HTMLTextAreaElement,
  AutosizeTextareaProps
>(
  (
    {
      value,
      onChange,
      onFocus: _onFocus,
      onBlur: _onBlur,
      shouldFocus,
      testId,
      bufferHeight = 0,
      minHeight = 0,
      maxVisibleRows,
      verticallyCenter = false,
      hideScrollbar = false,
      hideResizeHandle = false,
      submitOnEnter = false,
      submitHintId,
      className,
      style,
      onKeyDown: _onKeyDown,
      'aria-describedby': ariaDescribedByFromProps,
      ...props
    },
    ref,
  ) => {
    const forwardedRef = useForwardRef(ref);
    const generatedHintId = useId();
    const newlineHintId = submitHintId ?? generatedHintId;
    const shouldRenderSubmitHint = submitOnEnter && !submitHintId;
    const [isMultiline, setIsMultiline] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    useEffect(() => {
      if (shouldFocus && forwardedRef.current) {
        const textarea = forwardedRef.current;
        const timeout = setTimeout(
          () => textarea.focus({ preventScroll: true }),
          0,
        );
        return () => clearTimeout(timeout);
      }
    }, [shouldFocus, forwardedRef]);

    const shouldVerticalCenter =
      minHeight > 0 && verticallyCenter && !isMultiline;

    const resize = useCallback(() => {
      const textarea = forwardedRef.current;
      if (!textarea) return;

      // We need to reset the height briefly so that we can correctly
      // recalculate the element's scrollHeight.
      setHeight(textarea, 0);

      const scrollHeight = textarea.scrollHeight;
      const computedStyle = getComputedStyle(textarea);
      const resolvedLineHeight = getLineHeight(computedStyle);
      const paddingBlock =
        parseComputedCssLength(computedStyle.paddingTop) +
        parseComputedCssLength(computedStyle.paddingBottom);
      const defaultPaddingBlock = Math.max(
        parseComputedCssLength(computedStyle.paddingTop),
        parseComputedCssLength(computedStyle.paddingBottom),
      );
      const borderBlock =
        parseComputedCssLength(computedStyle.borderTopWidth) +
        parseComputedCssLength(computedStyle.borderBottomWidth);
      const singleRowScrollHeight = resolvedLineHeight + paddingBlock;
      const nextIsMultiline =
        resolvedLineHeight > 0
          ? scrollHeight > singleRowScrollHeight + 1
          : textarea.value.includes('\n');
      const nextShouldVerticalCenter =
        minHeight > 0 && verticallyCenter && !nextIsMultiline;
      const centeredPaddingBlock = Math.max(
        defaultPaddingBlock,
        (minHeight - borderBlock - resolvedLineHeight) * 0.5,
      );

      setIsMultiline(nextIsMultiline);

      if (nextShouldVerticalCenter) {
        textarea.style.setProperty(
          'padding-block',
          `${centeredPaddingBlock}px`,
        );
      } else {
        if (style?.paddingBlock !== undefined) {
          textarea.style.setProperty(
            'padding-block',
            toCssValue(style.paddingBlock),
          );
        } else {
          textarea.style.removeProperty('padding-block');
        }
      }

      let nextHeight = Math.max(minHeight, scrollHeight) + bufferHeight;

      const maxVisibleHeight =
        maxVisibleRows && resolvedLineHeight > 0
          ? resolvedLineHeight * maxVisibleRows + paddingBlock + borderBlock
          : null;

      // We then set the height directly, outside of the render loop.
      if (maxVisibleHeight !== null) {
        const nextIsOverflowing = nextHeight > maxVisibleHeight;
        setIsOverflowing(nextIsOverflowing);
        nextHeight = Math.min(nextHeight, maxVisibleHeight);
        textarea.style.overflowY = nextIsOverflowing ? 'auto' : 'hidden';
      } else {
        setIsOverflowing(false);
        textarea.style.overflowY = '';
      }

      setHeight(textarea, nextHeight);
    }, [
      forwardedRef,
      bufferHeight,
      minHeight,
      maxVisibleRows,
      style,
      verticallyCenter,
    ]);

    // Run before paint so first frame uses the correct height (controlled + mount).
    useLayoutEffect(() => {
      resize();
    }, [resize, shouldVerticalCenter, value]);

    const onFocus = useCallback<FocusEventHandler<HTMLTextAreaElement>>(
      (e) => {
        resize();
        _onFocus?.(e);
      },
      [_onFocus, resize],
    );

    const onBlur = useCallback<FocusEventHandler<HTMLTextAreaElement>>(
      (e) => {
        // Remeasure when focus leaves (e.g. click-away with empty / stale inline height).
        resize();
        _onBlur?.(e);
      },
      [_onBlur, resize],
    );

    const onKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
      (event) => {
        _onKeyDown?.(event);

        if (
          submitOnEnter &&
          !event.defaultPrevented &&
          isSubmitEvent(event) &&
          !event.shiftKey &&
          !event.nativeEvent.isComposing
        ) {
          event.preventDefault();
          event.currentTarget.form?.requestSubmit();
        }
      },
      [_onKeyDown, submitOnEnter],
    );

    const ariaDescribedBy = [
      ariaDescribedByFromProps,
      submitOnEnter ? newlineHintId : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    const textarea = (
      <textarea
        ref={forwardedRef as ReactRef<HTMLTextAreaElement>}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid={testId}
        data-is-multiline={isMultiline}
        data-is-overflowing={isOverflowing}
        className={cx(
          styles.root,
          hideScrollbar && styles.hideScrollbar,
          hideResizeHandle && styles.hideResizeHandle,
          shouldVerticalCenter && styles.verticalCenter,
          className,
        )}
        style={style}
        {...props}
        onKeyDown={onKeyDown}
        aria-describedby={ariaDescribedBy || undefined}
      />
    );

    if (!shouldRenderSubmitHint) {
      return textarea;
    }

    // Keep the textarea as the caller's layout child so grid overlays still work.
    return (
      <>
        {textarea}
        <AutosizeTextareaSubmitHint id={newlineHintId} />
      </>
    );
  },
);
